<script setup lang="ts">
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

import { SearchableSelectSectionSimpleTree, SectionApplianceCard } from "@voltalis/ui-kit/components";
import { inject, watch } from "vue";

import {
  IMoveSectionAppliancesWizardInject,
  MoveSectionAppliancesWizardInject,
} from "@/app/tools/new-section-management/site-sections-tree/section-appliances/move-section-appliances-wizard/move-section-appliances-wizard.types";
import { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import { IWizardInject, WizardInject } from "@/components/ui/wizard/wizard.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import Message from "primevue/message";

const wizardContext = inject<IWizardInject>(WizardInject);
const context = inject<IMoveSectionAppliancesWizardInject>(MoveSectionAppliancesWizardInject)!;

const { t, k } = useTypedIntl();

function showRadioButtonCb(node: IVirtualizedTreeNode<TSiteSectionsTreeData>): boolean {
  return !node.children?.length;
}

function isNodeDisabled(node: IVirtualizedTreeNode<TSiteSectionsTreeData>): boolean {
  return node.data?.id === context.initialSelectedSection?.id;
}

function handleSelectTargetSection(selectedSection: TSiteSectionsTreeData) {
  context.selectedTargetSection = selectedSection ?? undefined;
}

watch(
  () => context.selectedTargetSection,
  (newTargetSection) => {
    if (!newTargetSection || newTargetSection.id === context.initialSelectedSection?.id) {
      wizardContext?.setNextDisabled(true);
    } else {
      wizardContext?.setNextDisabled(false);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="flex flex-col gap-6"
    :data-test-id="DATA_TEST_IDS.components.moveSectionAppliancesWizard.selectSectionTargetStep.wizardTab"
  >
    <div class="px-6 mt-1">
      <Message
        :closable="false"
        :data-test-id="DATA_TEST_IDS.components.moveSectionAppliancesWizard.selectSectionTargetStep.message"
        severity="info"
        class="m-0"
      >
        {{ t(k.tools.siteManagement.moveSiteAppliancesWizard.message({ count: context.selectedAppliances.length })) }}
      </Message>
    </div>

    <div class="border-t border-b flex flex-row h-[513px]">
      <div class="px-6 pt-4 border-r h-full flex flex-col gap-[10px] w-[286px]">
        <small class="uppercase">
          {{ t(k.tools.siteManagement.moveSiteAppliancesWizard.toMigrate) }}
        </small>

        <div
          class="flex flex-col gap-[10px] overflow-y-auto h-full pb-4"
          :data-test-id="
            DATA_TEST_IDS.components.moveSectionAppliancesWizard.selectSectionTargetStep.selectedAppliancesList
          "
        >
          <SectionApplianceCard
            v-for="(appliance, index) in context.selectedAppliances"
            :key="index"
            :appliance="appliance"
          />
        </div>
      </div>

      <div class="px-6 py-4 h-full w-[570px] gap-[10px] flex flex-col">
        <SearchableSelectSectionSimpleTree
          :initial-selected-section="context.initialSelectedSection"
          :site-sections-tree="context.siteSectionsTree"
          :showRadioButtonCb="showRadioButtonCb"
          :isNodeDisabled="isNodeDisabled"
          @selection="handleSelectTargetSection"
        />
      </div>
    </div>
  </div>
</template>
